import React, { useCallback, useMemo } from 'react';
import { Row } from 'antd';
import PropTypes from 'prop-types';
import { FormHelpers } from 'ontraccr-common';

import { PlusOutlined } from '@ant-design/icons';
import ConditionalRenderingBuilder from './ConditionalRenderingBuilder';
import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import OnTraccrSwitch from '../../../common/inputs/OnTraccrSwitch';
import OnTraccrCheckbox from '../../../common/inputs/OnTraccrCheckbox';

export default function ConditionalRenderingRow({
  onChange,
  onFormulaChange,
  hasConditionalRendering,
  conditionalRenderingFormula,
  sections = [],
  id,
  customers = [],
  projects = [],
  users = [],
  costcodes = [],
  phases = [],
  projectIdMap = {},
  vendors = [],
  equipment = [],
  formTemplates = [],
  labels = [],
  contactAddressBooks = {},
  buckets = [],
  scheduleCategories = [],
  formStatuses = [],
  boardStatuses = [],
  boardColorLegend = [],
  materials = [],
  showCheckbox = true,
  isSection = false,
  showType = true,
  additionalFields = [],
  FormulaWrapperComponent = React.Fragment,
  isAdvancedSearch = false,
}) {
  const type = isSection ? 'section' : 'field';

  const formulas = useMemo(() => {
    if (Array.isArray(conditionalRenderingFormula)) return conditionalRenderingFormula;
    if (conditionalRenderingFormula) return [conditionalRenderingFormula];
    return [{}];
  }, [conditionalRenderingFormula]);

  const hasOR = useMemo(() => (
    formulas.some((formula) => formula.type === 'OR')
  ), [formulas]);

  const onInvidualFormulaChange = useCallback((index) => (newFormula) => {
    const newFormulas = [...formulas];
    newFormulas[index] = newFormula;
    onFormulaChange(newFormulas);
  }, [formulas, onFormulaChange]);

  const shouldShowAddButton = useCallback((index) => {
    if (index !== formulas.length - 1) return false;
    // Only show the add button if the current formula is complete
    return FormHelpers.isIndividualConditionalRenderingFormulaComplete(formulas[index]);
  }, [formulas]);

  const onAddClicked = useCallback(() => {
    onFormulaChange([...formulas, {
      type: hasOR ? 'OR' : 'AND',
    }]);
  }, [formulas, onFormulaChange, hasOR]);

  const onDeleteClicked = useCallback((index) => () => {
    const newFormulas = [...formulas];
    newFormulas.splice(index, 1);
    onFormulaChange(newFormulas);
  }, [formulas, onFormulaChange]);

  const relevantSections = useMemo(() => (
    isSection
      ? sections.filter((section) => section.id !== id)
      : sections
  ), [sections, id, isSection]);

  const onTypeChange = useCallback((newType) => {
    onFormulaChange(formulas.map((formula) => ({
      ...formula,
      type: newType ? 'OR' : 'AND',
    })));
  }, [onFormulaChange, formulas]);

  return (
    <>
      { showCheckbox && (
        <OnTraccrCheckbox
          style={{ marginTop: 10 }}
          onChange={onChange}
          value={hasConditionalRendering}
          label={`Show this ${type} conditionally`}
          hoverNode={`Allows this ${type} to be shown based off another value`}
        />
      )}
      { hasConditionalRendering && (
        <>
          {!!showType && (
            <Row style={{ marginTop: 10 }}>
              <OnTraccrSwitch
                unCheckedChildren="AND"
                checkedChildren="OR"
                onChange={onTypeChange}
                value={hasOR}
              />
            </Row>
          )}
          {formulas.map((formula, index) => (
            <FormulaWrapperComponent index={index}>
              <ConditionalRenderingBuilder
                id={id}
                sections={relevantSections}
                onChange={onInvidualFormulaChange(index)}
                conditionalRenderingFormula={formula}
                customers={customers}
                projects={projects}
                users={users}
                costcodes={costcodes}
                phases={phases}
                projectIdMap={projectIdMap}
                vendors={vendors}
                equipment={equipment}
                formTemplates={formTemplates}
                labels={labels}
                contactAddressBooks={contactAddressBooks}
                buckets={buckets}
                scheduleCategories={scheduleCategories}
                formStatuses={formStatuses}
                boardStatuses={boardStatuses}
                boardColorLegend={boardColorLegend}
                showDelete={formulas.length > 1}
                showAdd={shouldShowAddButton(index)}
                onAddClicked={onAddClicked}
                onDeleteClicked={onDeleteClicked(index)}
                isSection={isSection}
                additionalFields={additionalFields}
                isAdvancedSearch={isAdvancedSearch}
                materials={materials}
              />
            </FormulaWrapperComponent>
          ))}
          { !formulas.length && (
            <OnTraccrButton
              type="primary"
              style={{ marginTop: 15 }}
              icon={<PlusOutlined />}
              title="Add Condition"
              onClick={onAddClicked}
            />
          )}
        </>
      )}
    </>
  );
}

ConditionalRenderingRow.propTypes = {
  onChange: PropTypes.func.isRequired,
  onFormulaChange: PropTypes.func.isRequired,
  hasConditionalRendering: PropTypes.bool.isRequired,
  conditionalRenderingFormula: PropTypes.oneOf([
    PropTypes.shape({
      field: PropTypes.string,
      operator: PropTypes.string,
      value: PropTypes.string,
    }),
    PropTypes.arrayOf(PropTypes.shape({
      field: PropTypes.string,
      operator: PropTypes.string,
      value: PropTypes.string,
    })),
  ]),
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  id: PropTypes.string,
  users: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  customers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  costcodes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  phases: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  projects: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  projectIdMap: PropTypes.shape({}),
  vendors: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  labels: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  })),
  equipment: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  formTemplates: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  contactAddressBooks: PropTypes.shape({}),
  buckets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  scheduleCategories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  formStatuses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  boardStatuses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  boardColorLegend: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    color: PropTypes.string,
  })),
  materials: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  showCheckbox: PropTypes.bool,
  isSection: PropTypes.bool,
  showType: PropTypes.bool,
  additionalFields: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    selectedType: PropTypes.string,
    configProps: PropTypes.shape({
      dataType: PropTypes.string,
    }),
  })),
  FormulaWrapperComponent: PropTypes.func,
  isAdvancedSearch: PropTypes.bool,
};

ConditionalRenderingRow.defaultProps = {
  sections: [],
  id: null,
  conditionalRenderingFormula: null,
  customers: [],
  projects: [],
  users: [],
  costcodes: [],
  phases: [],
  projectIdMap: {},
  vendors: [],
  equipment: [],
  formTemplates: [],
  labels: [],
  contactAddressBooks: {},
  buckets: [],
  scheduleCategories: [],
  formStatuses: [],
  boardStatuses: [],
  boardColorLegend: [],
  materials: [],
  showCheckbox: true,
  isSection: false,
  showType: true,
  additionalFields: [],
  FormulaWrapperComponent: React.Fragment,
  isAdvancedSearch: false,
};
